// src/services/analyticsService.js
import api from "./apiClient";
/**
 * @file analyticsService.js
 * @description Service for fetching dashboard Key Performance Indicators (KPIs) and chart data.
 * Implements a Multi-Key caching strategy with TTL to optimize performance.
 */


// Direct URL as requested
const API_URL = "https://utility.wiiz.it/api/admin/kpi/super-admin/dashboard";

const CACHE = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 Minutes

/**
 * Generate a unique cache key based on params
 */
const getCacheKey = (timePeriod, customRange, planFilter) => {
  return JSON.stringify({
    timePeriod,
    customRange,
    planFilter: planFilter || null, // Ensure explicit null for consistency
  });
};

const MOCK = {
  kpis: {
    totalExecutions: 1360000,
    avgLatencyMs: 342,
    successRatePct: 99.2,
    totalApiCalls: 4800000,
    totalErrors: 12240,
  },
  executionsOverTime: {
    labels: ["Oct 1", "Oct 10", "Oct 20", "Oct 30"],
    series: [120000, 160000, 200000, 240000],
  },
  moduleAdoption: {
    labels: ["Workflow", "Insights", "Agents", "Observability"],
    series: [45, 28, 18, 9],
  },
  executionsTrend: {
    labels: [
      "Day 1",
      "Day 5",
      "Day 10",
      "Day 15",
      "Day 20",
      "Day 25",
      "Day 30",
    ],
    series: [
      {
        name: "Executions",
        data: [18000, 21000, 24000, 26000, 28000, 30000, 33000],
      },
    ],
  },
  successRate: {
    labels: [
      "Day 1",
      "Day 5",
      "Day 10",
      "Day 15",
      "Day 20",
      "Day 25",
      "Day 30",
    ],
    series: [
      {
        name: "Success Rate %",
        data: [98.5, 99.0, 99.2, 99.1, 99.3, 99.4, 99.5],
      },
    ],
  },
  workflowLatency: {
    labels: [
      "Day 1",
      "Day 5",
      "Day 10",
      "Day 15",
      "Day 20",
      "Day 25",
      "Day 30",
    ],
    series: [
      { name: "Latency (ms)", data: [350, 340, 335, 330, 325, 320, 315] },
    ],
  },
  errorDistribution: {
    labels: ["API Error", "Timeout", "Validation", "Other"],
    series: [45, 25, 20, 10],
  },
  failuresByModule: {
    labels: ["Workflow", "Agents", "Insights", "Compliance"],
    series: [{ name: "Failures", data: [120, 80, 60, 40] }],
  },
  agentUsage: {
    labels: ["Agent 1", "Agent 2", "Agent 3", "Agent 4"],
    series: [35, 25, 20, 20],
  },
  storageUsage: {
    labels: [
      "Day 1",
      "Day 5",
      "Day 10",
      "Day 15",
      "Day 20",
      "Day 25",
      "Day 30",
    ],
    series: [
      {
        name: "Storage (GB)",
        data: [2400, 2450, 2500, 2550, 2600, 2650, 2700],
      },
    ],
  },
  cpuMemory: {
    labels: [
      "Day 1",
      "Day 5",
      "Day 10",
      "Day 15",
      "Day 20",
      "Day 25",
      "Day 30",
    ],
    cpu: [{ name: "CPU %", data: [45, 48, 50, 52, 55, 53, 54] }],
    memory: [{ name: "Memory %", data: [60, 62, 64, 66, 68, 67, 69] }],
  },
};

/**
 * Fetches Dashboard Data.
 * @param {string} timePeriod - "all_time", "daily", etc.
 * @param {boolean} forceRefresh - If true, ignores cache and hits API.
 */
/**
 * Fetches dashboard KPI data with caching.
 * 
 * @param {string} timePeriod - The selected time period (e.g., 'weekly').
 * @param {Object|null} customRange - Custom date range {start, end} if applicable.
 * @param {string|null} planFilter - Filter by plan type.
 * @param {boolean} forceRefresh - Whether to bypass cache.
 * @returns {Promise<Object>} Dashboard data including KPIs, platform summary, and execution history.
 */
const IN_FLIGHT_REQUESTS = new Map();

/**
 * Fetches dashboard KPI data with caching and deduplication.
 * 
 * @param {string} timePeriod - The selected time period (e.g., 'weekly').
 * @param {Object|null} customRange - Custom date range {start, end} if applicable.
 * @param {string|null} planFilter - Filter by plan type.
 * @param {boolean} forceRefresh - Whether to bypass cache.
 * @returns {Promise<Object>} Dashboard data including KPIs, platform summary, and execution history.
 */
export async function fetchDashboardKpis(
  timePeriod = "daily",
  customRange = null,
  planFilter = null,
  forceRefresh = false
) {
  const cacheKey = getCacheKey(timePeriod, customRange, planFilter);
  const now = Date.now();

  // 0. Deduplication: Check if request is already in flight
  if (IN_FLIGHT_REQUESTS.has(cacheKey)) {
    console.log("⏳ Reusing in-flight request for:", cacheKey);
    return IN_FLIGHT_REQUESTS.get(cacheKey);
  }

  const cachedEntry = CACHE.get(cacheKey);

  // 1. Check Cache with TTL
  if (
    !forceRefresh &&
    cachedEntry &&
    now - cachedEntry.timestamp < CACHE_TTL
  ) {
    console.log("⚡ Returning Cached Dashboard Data for:", cacheKey);
    return cachedEntry.data;
  }

  // Create the request promise
  const requestPromise = (async () => {
    try {
      console.log(
        `🌐 Fetching API (Force: ${forceRefresh}, Period: ${timePeriod}, Plan: ${planFilter})...`
      );

      const payload = {};
      if (timePeriod === "custom") {
        // Ensure specific structure for custom range
        if (customRange?.start && customRange?.end) {
          payload.start_date = customRange.start;
          payload.end_date = customRange.end;
        } else {
          console.warn("Skipping Dashboard API call: Custom range incomplete", customRange);
          return null; // Don't call API if incomplete
        }
      } else {
        payload.time_period = timePeriod;
      }

      if (planFilter) {
        payload.plan_filter = planFilter;
      }

      const response = await api.post(API_URL, payload);

      // 2. Process Response
      let result = null;
      if (response.data && response.data.platform_summary) {
        result = response.data;
        // console.log("-------------------------------------")
        // console.log(result)
      } else if (response.data && response.data.data) {
        result = response.data.data;
      }

      // 3. Update Cache
      if (result) {
        CACHE.set(cacheKey, {
          data: result,
          timestamp: Date.now(),
        });
        return result;
      }

      return null;
    } catch (error) {
      console.error("Dashboard API Error:", error);
      return null;
    }
  })();

  // Store in flight map
  IN_FLIGHT_REQUESTS.set(cacheKey, requestPromise);

  // Clean up when done
  return requestPromise.finally(() => {
    IN_FLIGHT_REQUESTS.delete(cacheKey);
  });
}

/**
 * Fetches available plans from storage or API.
 * Optimized to reduce API calls by using localStorage.
 * @returns {Promise<string[]>} List of available plan keys.
 */
export async function getAvailablePlans(timePeriod = "monthly", customRange = null, forceRefresh = false) {
  // If custom and no range, we CANNOT fetch plans from API because API requires range.
  if (timePeriod === "custom" && (!customRange?.start || !customRange?.end)) {
    // Return empty array or cached if available, but avoiding API call is key
    return [];
  }
  const cacheKey = `available_plans_v2_${timePeriod}_${customRange ? JSON.stringify(customRange) : ''}`;

  if (forceRefresh) {
    localStorage.removeItem(cacheKey);
  } else {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        localStorage.removeItem(cacheKey);
      }
    }
  }

  // Fallback to fetching dashboard KPIs if not in storage
  console.log(`Fetching plans for period: ${timePeriod}...`);
  // Pass the requested timePeriod and forceRefresh to ensure fresh data if requested
  const data = await fetchDashboardKpis(timePeriod, customRange, null, forceRefresh);

  if (data && data.platform_summary && data.platform_summary.plan_distribution) {
    const rawDistribution = data.platform_summary.plan_distribution;
    // console.log(`RAW PLAN DATA (${timePeriod}):`, rawDistribution);

    // Filter: Include ONLY plans with Value > 0
    const plans = Object.entries(rawDistribution)
      .filter(([planName, count]) => {
        const numCount = Number(count);
        return numCount > 0;
      })
      .map(([planName]) => planName.charAt(0).toUpperCase() + planName.slice(1));

    // console.log(`FINAL FILTERED PLANS (${timePeriod}):`, plans);

    localStorage.setItem(cacheKey, JSON.stringify(plans));
    return plans;
  }
  return [];
}

export async function fetchAnalyticsOverview() {
  try {
    const res = await api.get("/analytics/overview");
    if (res?.data) return res.data;
  } catch {
    console.warn("Analytics API unavailable, using mock data.");
  }
  return MOCK;
}

const OP_CACHE = {
  data: null,
  timePeriod: null,
  lastFetched: 0
};

/**
 * Get operational insights with Caching
 */
/**
 * Fetches operational insights data with caching.
 * 
 * @param {string} timePeriod - Time period for metrics.
 * @param {boolean} forceRefresh - Whether to bypass cache.
 * @returns {Promise<Object>} Operational metrics data.
 */
const USAGE_API_URL = "https://utility.wiiz.it/api/admin/kpi/super-admin/usage-analytics";

export async function getOperationalInsights(timePeriod = "weekly", customRange = null, planFilter = null, forceRefresh = false) {
  try {
    // 1. Check Cache
    // We reuse the same cache map but need a unique key prefix/structure to avoid collisions with dashboard data if parameters overlap
    // However, existing getCacheKey uses JSON.stringify which is generic. 
    // Let's modify getCacheKey or just prepend "OP_" to the timePeriod to make it unique within the same Map.
    const cacheKey = `OP_${getCacheKey(timePeriod, customRange, planFilter)}`;
    const cachedEntry = CACHE.get(cacheKey);
    const now = Date.now();

    if (!forceRefresh && cachedEntry && now - cachedEntry.timestamp < CACHE_TTL) {
      console.log("⚡ Returning Cached Operational Data for:", cacheKey);
      return cachedEntry.data;
    }

    const payload = {};
    if (timePeriod === "custom") {
      if (customRange?.start && customRange?.end) {
        payload.start_date = customRange.start;
        payload.end_date = customRange.end;
      } else {
        console.warn("Skipping Operational API call: Custom range incomplete");
        return null;
      }
    } else {
      payload.time_period = timePeriod;
    }

    if (planFilter) {
      payload.plan_filter = planFilter;
    }

    console.log("Fetching Usage Analytics with payload:", payload);
    const response = await api.post(USAGE_API_URL, payload);
    const apiData = response.data?.data;

    if (!apiData) throw new Error("No data returned");

    // Helper to get dates and values
    const getDates = (arr) => arr.map(i => i.date);
    const getValues = (arr, key) => arr.map(i => i[key]);

    // 1. Map KPIs
    const kpis = {
      exec: apiData.summary_kpis.total_executions,
      successRate: parseFloat(apiData.summary_kpis.success_rate),
      failed: apiData.summary_kpis.failed_executions,
      latency: parseFloat(apiData.summary_kpis.avg_latency),
      failureRate: apiData.summary_kpis.failure_rate,
      storage: apiData.summary_kpis.storage_used,
      users: apiData.summary_kpis.total_users
    };

    // 2. Map Charts
    // Execution Volume
    const executionsTrend = {
      labels: getDates(apiData.execution_volume_trend),
      series: [{ name: "Executions", data: getValues(apiData.execution_volume_trend, "count") }]
    };

    // Success Rate
    const successRate = {
      labels: getDates(apiData.success_rate_trend),
      series: [{ name: "Success Rate %", data: getValues(apiData.success_rate_trend, "success_rate") }]
    };

    // Latency (Avg and P95)
    // Latency (Avg and P95)
    // Converted to seconds as per user request
    const workflowLatency = {
      labels: getDates(apiData.latency_trend),
      series: [
        { name: "Avg Latency (s)", data: getValues(apiData.latency_trend, "avg_latency").map(v => (v / 1000).toFixed(2)) },
        { name: "P95 Latency (s)", data: getValues(apiData.latency_trend, "p95_latency").map(v => (v / 1000).toFixed(2)) }
      ]
    };

    // Error Distribution
    const errorDistribution = {
      labels: apiData.failure_type_distribution.map(i => i.type),
      series: apiData.failure_type_distribution.map(i => i.count)
    };

    // Failures by module (Node Metrics errors)
    // User requested to show ALL nodes whose data is there, not just those with errors.
    const failuresByModule = {
      labels: apiData.node_metrics.map(n => n.node_name),
      series: [{ data: apiData.node_metrics.map(n => n.error_count) }]
    };

    const finalData = {
      kpis,
      executionsTrend,
      successRate,
      workflowLatency,
      errorDistribution,
      failuresByModule,
      nodeMetrics: apiData.node_metrics,
      tenantMetrics: apiData.tenant_performance_metrics
    };

    // 3. Update Cache
    CACHE.set(cacheKey, {
      data: finalData,
      timestamp: Date.now(),
    });

    return finalData;

  } catch (error) {
    console.error("Usage Analytics API Error:", error);
    throw error; // Propagate error to UI instead of showing mock data
  }
}

/**
 * Fetches detailed workflows for a specific tenant and project.
 * Used in the Operational Analytics drill-down modal.
 * 
 * @param {string} tenantId - The UUID of the tenant/org.
 * @param {string} projectId - The UUID of the project.
 * @param {string} timePeriod - Time filter (e.g., 'monthly').
 * @returns {Promise<Object>} Object containing workflows list and metadata.
 */
export async function fetchProjectWorkflows(tenantId, projectId, timePeriod = "monthly", customRange = null) {
  try {
    let url = `https://utility.wiiz.it/api/admin/kpi/super-admin/tenant/${tenantId}/project/${projectId}/workflows?time_period=${timePeriod}`;
    if (timePeriod === "custom" && customRange?.start && customRange?.end) {
      url += `&start_date=${customRange.start}&end_date=${customRange.end}`;
    }
    console.log(`fetching workflows for tenant ${tenantId} project ${projectId}`);
    const response = await api.get(url);
    if (response.data && response.data.status === "success") {
      return response.data.data; // Returns { org_id, project_id, workflows: [...], ... }
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch project workflows:", error);
    return null;
  }
}


/**
 * Fetches list of projects for a specific tenant.
 * used in the Operational Analytics drill-down modal (Tenant View).
 * 
 * @param {string} tenantId - The UUID of the tenant/org.
 * @param {string} timePeriod - Time filter (e.g., 'monthly').
 * @returns {Promise<Object>} Object containing projects list.
 */
export async function fetchTenantProjects(tenantId, timePeriod = "monthly", customRange = null) {
  try {
    let url = `https://utility.wiiz.it/api/admin/kpi/super-admin/tenant/${tenantId}/projects?time_period=${timePeriod}`;
    if (timePeriod === "custom" && customRange?.start && customRange?.end) {
      url += `&start_date=${customRange.start}&end_date=${customRange.end}`;
    }
    console.log(`fetching projects for tenant ${tenantId}`);
    const response = await api.get(url);
    if (response.data && response.data.status === "success") {
      return response.data.data; // Returns { org_id, projects: [...], timestamp: ... }
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch tenant projects:", error);
    return null;
  }
}

/**
 * Fetches analytics for a specific node type.
 * used in the Operational Analytics drill-down modal (Node Detail).
 * 
 * @param {string} nodeType - The type of the node (e.g., 'ChatNode').
 * @param {string} timePeriod - Time filter (e.g., 'monthly').
 * @param {string} planFilter - Optional plan filter.
 * @returns {Promise<Object>} Object containing node latency trend and stats.
 */
export async function fetchNodeAnalytics(nodeType, timePeriod = "monthly", planFilter = null, customRange = null) {
  try {
    let url = `https://utility.wiiz.it/api/admin/kpi/super-admin/node-analytics/${nodeType}?time_period=${timePeriod}`;
    if (timePeriod === "custom" && customRange?.start && customRange?.end) {
      url += `&start_date=${customRange.start}&end_date=${customRange.end}`;
    }
    // if (planFilter) {
    //   url += `&plan_filter=${planFilter}`;
    // }
    console.log(`fetching analytics for node type ${nodeType}`);
    const response = await api.get(url);
    if (response.data && response.data.status === "success") {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error(`Failed to fetch analytics for node ${nodeType}:`, error);
    return null;
  }
}

function getMockOperationalData() {
  return {
    executionsTrend: MOCK.executionsTrend,
    successRate: MOCK.successRate,
    workflowLatency: MOCK.workflowLatency,
    errorDistribution: MOCK.errorDistribution,
    failuresByModule: MOCK.failuresByModule,
    kpis: {
      exec: "125,000",
      successRate: 98.5,
      failed: 456,
      latency: 240,
      failureRate: "0.45%",
      storage: "450 GB",
      users: "1,200"
    },
    // Mocking the new arrays for fallback
    nodeMetrics: [
      { node_name: "LLM Generation", executions: 15420, success_rate: "99.2%", avg_latency: 4500, content_relevance: "0.89", retries: 120, timeout_rate: "0.05%", error_count: 12 },
      { node_name: "Vector Search", executions: 12300, success_rate: "99.8%", avg_latency: 200, content_relevance: "0.92", retries: 10, timeout_rate: "0.01%", error_count: 5 },
      { node_name: "API Connector", executions: 8500, success_rate: "95.5%", avg_latency: 1200, content_relevance: "-", retries: 340, timeout_rate: "1.20%", error_count: 150 },
      { node_name: "Code Execution", executions: 3200, success_rate: "88.0%", avg_latency: 800, content_relevance: "-", retries: 50, timeout_rate: "0.50%", error_count: 85 }
    ],
    tenantMetrics: [
      { tenant: "Acme Corp", projects: 12, workflows: 45, total_executions: 12500, execution_success_rate: "99.1%" },
      { tenant: "Globex Inc", projects: 5, workflows: 18, total_executions: 4200, execution_success_rate: "97.5%" },
      { tenant: "Soylent Corp", projects: 8, workflows: 22, total_executions: 8900, execution_success_rate: "98.8%" },
      { tenant: "Initech", projects: 3, workflows: 7, total_executions: 1500, execution_success_rate: "96.2%" }
    ]
  };
}
